import SubscriptionPackageCollectionView
					   from 'scripts/defa/subscription-packages/view-subscription-package-collection-view';
import ActiveContainer from 'scripts/poppet/active-container';
import template        from 'templates/subscriptionPackageContainer.jst.html';

/**
 * defa.views.SubscriptionPackageContainer
 * A container for selecting subscription packages, fakes being an active container with two forms.
 * @version 1.2.0
 */


const ENTER_KEY = 13;

const channel = Bb.Radio.channel( 'objects' );

const selectors = {
	container        : '[data-region=selection-container]',
	feedbackPromotion: '[data-container=promo-feedback]',
	inputPromotion   : '[data-input=selection-promo-code]',
	buttonPromotion  : '[data-click=selection-promotion]',
};

const SubscriptionPackageContainer = ActiveContainer.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template        : template,
	className       : 'card',
	noEnterKeypress : true,
	noInitValidation: true,
	attributes      : {
		'data-container': 'subscription-package-selection',
	},
	
	ui: {
		code    : selectors.inputPromotion,
		promo   : selectors.buttonPromotion,
		feedback: selectors.feedbackPromotion,
	},
	
	regions: {
		container: selectors.container,
	},
	
	events: {
		'input    @ui.code' : 'onInputCode',
		'click    @ui.promo': 'onClickPromo',
		'keypress @ui.code' : 'onKeypressCode',
	},
	
	collectionEvents: {
		'error' : 'onCollectionError',
		'update': 'onCollectionUpdate',
	},
	
	templateContext: function () {
		return {
			fixedPrice         : this._getFixedPrice(),
			currentPackName    : this._getPackageName(),
			currentMonthlyPrice: this._getPackageMonthlyPrice(),
			currentPackagePrice : this._getPackagePackagePrice(),
			installerAddress   : this._getInstallerAddress(),
			isPackageFree : this._isPackageFree(),
			showCurrent        : this.options.showCurrent || false,
			
			context: this.options.context || '',
			prepaid: this.options.prepaid,
			hidePromotion: this.options.hidePromotion|| false,
		};
	},
	
	
	// ------------------------------------------------
	// Event handlers
	// ------------------------------------------------
	
	// collection event listeners
	
	/**
	 * Triggers if the collection recieves a non-successfull ajax call.
	 * @void
	 */
	onCollectionError: function ( collection, response ) {
		this.createPromoError( response.responseJSON.message );
	},
	
	/**
	 * Triggers when the collection updates with new models.
	 * @void
	 */
	onCollectionUpdate: function () {
		this.idle();
		this.createSubscriptionRadioButtons();
	},
	
	// @ui event listeners
	
	/**
	 * Triggers when a key is pressed while the promo code input field is focused.
	 * @void
	 */
	onKeypressCode: function ( event ) {
		
		
		if ( event.which === ENTER_KEY && this.ui.code.val().trim() ) {
			this.getPromotionalPackages();
		}
	},
	
	/**
	 * Triggers when the user inputs into the code field.
	 * @void
	 */
	onInputCode: function () {
		if ( this.ui.code.val().trim() ) {
			this.ui.promo.removeAttr( 'disabled' );
		} else {
			this.ui.promo.prop( 'disabled', true );
		}
	},
	
	/**
	 * Triggers when the user clicks the promo button.
	 * @void
	 */
	onClickPromo: function () {
		this.getPromotionalPackages();
	},
	
	// childView event listeners
	
	/**
	 * Triggers when a childView has been selected.
	 * @void
	 */
	onChildviewSelected: function ( childView ) {
		this.selected = childView.model.get( 'id' );
		this.enableSubmit();
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// template helper functions
	
	/**
	 * Gets an existing package name.
	 * @return {string}  This unit's subscription package's name.
	 */
	_getPackageName: function () {
		return ( this.options.subscriptionPackage )
			   ? this.options.subscriptionPackage.get( 'transientName' )
			   : '';
	},
	
	/**
	 * Gets an existing package montly price.
	 * @return {string}  This unit's subscription package's montly price.
	 */
	_getPackageMonthlyPrice: function () {
		return ( this.options.subscriptionPackage )
			   ? this.options.subscriptionPackage.get( 'transientFormatMonthlyPrice' )
			   : '';
	},
	
	_getPackagePackagePrice: function() {
		return ( this.options.subscriptionPackage )
			   ? this.options.subscriptionPackage.get( 'transientFormatPackagePrice' )
			   : '';
	},
	
	_getFixedPrice: function() {
		return ( this.options.subscriptionPackage )
			   ? this.options.subscriptionPackage.get( 'fixedPrice' )
			   : '';
	},
	
	_isPackageFree: function() {
		return ( this.options.subscriptionPackage )
			   ? this.options.subscriptionPackage.get( 'transientFree' )
			   : false;
	},
	
	/**
	 * Returns either company name or email address, depending on the installers invoiceTypes preference.
	 * @return {string} The company name or email address.
	 */
	_getInstallerAddress: function () {
		if ( this.options.installer ) {
			return ( _.contains( this.options.installer.get( 'invoiceTypes' ), 'EMAIL' ) )
				   ? this.options.installer.get( 'invoiceEmail' )
				   : this.options.installer.name();
		} else {
			return false;
		}
	},
	
	// views
	
	/**
	 * Attaches other views to this container.
	 * @void
	 */
	attachView: function () {
		this.createSubscriptionRadioButtons();
	},
	
	/**
	 * Fetches promo packages by promo-code and updates the collection.
	 * @void
	 */
	getPromotionalPackages: function () {
		let self = this;
		
		self.busy();
		
		let promotionCode = self.ui.code.val().trim();
		
		self.listenToOnce( self.collection, 'add', function ( model ) {
			
			// push promotionCode to the model, and update the channel models.
			model.set( 'promotionCode', promotionCode );
			channel.request( 'subscriptionPackages' ).add( model, { merge: true } );
		} );
		
		// fetch packages with promotional code
		
		// is this a little hacky? who should the promo go to? customer or installer?
		let promoCustomerId = ( self.options.installer )
							  ? self.options.installer.get( 'id' )
							  : self.options.owner;
		
		self.collection.fetchPromotional( {
			prepaid      : self.options.prepaid,
			unitId       : self.options.unit.get( 'id' ),
			customerId   : promoCustomerId, // self.options.installer.get( 'id' ),
			promotionCode: promotionCode,
		} );
	},
	
	/**
	 * Creates a feedback error message when a promotional code fails.
	 * @void
	 */
	createPromoError: function ( message ) {
		let html5 = '<p class="static error">%s</p>'
			.replace( '%s', message );
		
		this.ui.feedback.append( html5 );
		this.idle();
	},
	
	/**
	 * Attaches a new subscriptionPackageCollectionView to the container.
	 * @void
	 */
	createSubscriptionRadioButtons: function () {
		
		let subscriptionPackageCollectionView = new SubscriptionPackageCollectionView( _.extend( {
			collection: this.collection
		}, this.options ) );
		
		this.showChildView( 'container', subscriptionPackageCollectionView );
	},
	
	/**
	 * Submits the form.
	 * @void
	 */
	submit: function () {
		this.trigger( 'subscriptionPackage:selected', this.selected );
	},
	
} );


export default SubscriptionPackageContainer;